import React, { FC, useEffect, useState } from "react";
import { Input, Cascader, Button, Select, message } from "antd";
import { useWatchState, handler } from "@/store";
import BaseService from "@/service/base";
import { returnOrAddOrgIdByName } from "@/business/modules/organization";
import { CascaderOptionType } from "antd/lib/cascader";
import { createSelectOption } from "@/common";
import { SelectValue } from "antd/lib/select";
import "./OrgForm.less";

type OrgFormProps = {
  type: "org" | "company";
  userId: string;
  onFinish?: () => void;
  defaultData?: any;
  isUpdate?: boolean
};

const OrgForm: FC<OrgFormProps> = function ({
  userId,
  type,
  onFinish,
  defaultData = {},
  isUpdate = false
}) {
  const name = { org: "科研机构", company: "企业" }[type];
  const shortName = { org: "机构", company: "企业" }[type];
  const [[REGIONS = []]] = useWatchState(["$_REGIONS"]);
  const [[SCOPE_DIRECTION = []]] = useWatchState(["$_SCOPE_DIRECTION"]);

  async function getScopes(org_id: string) {
    const res: any = await BaseService.list({
      table: 'thtt_organization_scope',
      where: {
        org_id
      }
    })
    setScopes(res.data.result)
  }

  // 在这里加载默认的领域
  useEffect(() => {
    const { organization_id } = defaultData
    if (organization_id) getScopes(organization_id)
  }, [defaultData.organization_id])

  const [orgName, setOrgName] = useState(defaultData.name || "");
  const [contacts, setContacts] = useState(defaultData.contacts || "");
  const [contactsTel, setContactsTel] = useState(
    defaultData.contacts_tel || ""
  );
  const [contactsEmail, setContactsEmail] = useState(
    defaultData.contacts_email || ""
  );
  const [wechat, setEechat] = useState(defaultData.wechat || "");
  const [scopes, setScopes] = useState<
    {
      first_scope_id: string;
      second_scope_id: string;
    }[]
  >([]);
  const [addressInfo, setAddressInfo] = useState({
    province: defaultData.province || 0,
    city: defaultData.city || 0,
    county: defaultData.county || 0,
    provinceName: defaultData.provinceName || "",
    cityName: defaultData.cityName || "",
    countyName: defaultData.countyName || "",
  });

  const onChangeAddress = (
    value: string[],
    selectedOptions?: CascaderOptionType[] | undefined
  ) => {
    addressInfo.province = value[0];
    addressInfo.city = value[1];
    addressInfo.county = value[2];

    if (selectedOptions) {
      addressInfo.provinceName = selectedOptions[0].label as string;
      addressInfo.cityName =
        selectedOptions[1] && (selectedOptions[1].label as string);
      addressInfo.countyName =
        selectedOptions[2] && (selectedOptions[2].label as string);
    }
    setAddressInfo({ ...addressInfo });
  };

  const saveOrgScopes = async (org_id: string) => {
    await BaseService.remove({
      table: 'thtt_organization_scope',
      where: { org_id }
    })
    const scopeRes = await BaseService.adds({
      table: "thtt_organization_scope",
      data: JSON.stringify(
        scopes.map((scope) => ({
          org_id,
          ...scope,
        }))
      ),
    });
  };

  const onChangeScope = (value: SelectValue, options: any) => {
    const scopes = options.map((item: any) => {
      const { key, props } = item;
      return {
        first_scope_id: props.data.pid,
        second_scope_id: key,
      };
    });
    setScopes(scopes);
  };

  const onSubmit = async () => {
    if (!orgName) {
      message.error(`请输入${name}名称！`);
      return;
    }
    // 创建机构、机构用户
    const orgId = await returnOrAddOrgIdByName(orgName, userId, "", {
      ...addressInfo,
      contacts,
      contacts_tel: contactsTel,
      contacts_email: contactsEmail,
      wechat,
    });
    if (scopes.length) await saveOrgScopes(orgId);
    if (onFinish) onFinish();
    message.success('修改成功！')
  };

  return (
    <div className="p-orgForm child-top-sm">
      <Input
        value={orgName}
        disabled={isUpdate}
        onChange={(e) => setOrgName(e.target.value)}
        placeholder={`请输入${name}名称`}
      />
      <Cascader
        className={addressInfo.provinceName ? "mockInput w-full" : "w-full"}
        onChange={onChangeAddress}
        options={REGIONS}
        changeOnSelect
        loadData={
          type === "org" ? handler.onLoadRegionLevel2 : handler.onLoadRegion
        }
        placeholder={
          addressInfo.provinceName
            ? [
                addressInfo.provinceName,
                addressInfo.cityName,
                addressInfo.countyName,
              ].filter(Boolean).join(" / ")
            : `请选择${shortName}所在地`
        }
      />
      <Select
        placeholder={
          type === "company" ? "请选择企业所属领域" : "请选择机构研究方向"
        }
        value={scopes.map(({ second_scope_id }) => second_scope_id)}
        mode="multiple"
        className="w-full"
        onChange={onChangeScope}
      >
        {createSelectOption(
          (SCOPE_DIRECTION || []).map((val: any) => ({
            ...val,
            en: val.name_en,
          }))
        )}
      </Select>
      <Input
        value={contacts}
        onChange={(e) => setContacts(e.target.value)}
        placeholder="请输入联系人姓名"
      />
      <Input
        value={contactsTel}
        onChange={(e) => setContactsTel(e.target.value)}
        placeholder="请输入联系电话"
      />
      <Input
        value={contactsEmail}
        onChange={(e) => setContactsEmail(e.target.value)}
        placeholder="请输入联系邮箱"
      />
      <Input
        value={wechat}
        onChange={(e) => setEechat(e.target.value)}
        placeholder="请输入联系人微信"
      />
      <Button className="w-full m-md-t" type="primary" onClick={onSubmit}>
        提交
      </Button>
    </div>
  );
};

export default React.memo(OrgForm);
